import { useSEO } from '../../utils/seo';
import HeroSection from './components/HeroSection';
import PainPointsSection from './components/PainPointsSection';
import ProductsSection from './components/ProductsSection';
import BenefitsSection from './components/BenefitsSection';
import ProcessSection from './components/ProcessSection';
import TestimonialsSection from './components/TestimonialsSection';
import GallerySection from './components/GallerySection';
import AboutSection from './components/AboutSection';
import ContactSection from './components/ContactSection';

const HomePage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: 'GutterBrush Simple Gutter Guards - Easy Install Gutter Protection Since 2004',
    description: 'GutterBrush Simple Gutter Guards offers low-cost, easy-install gutter leaf protection. Available in 6 diameters for any residential or commercial gutter. Made in USA since 2004. Most homes protected for $300-$400.',
    keywords: 'gutter guards, gutter protection, leaf guards, gutter brush, clog prevention',
    canonical: `${siteUrl}/`,
    ogType: 'website',
    ogUrl: `${siteUrl}/`,
    ogTitle: 'GutterBrush Simple Gutter Guards - Easy Install Gutter Protection',
    ogDescription: 'Low-cost, easy-install gutter leaf protection. Available in 6 diameters. Made in USA since 2004. Most homes protected for $300-$400.',
    twitterCard: 'summary_large_image',
    twitterTitle: 'GutterBrush Simple Gutter Guards - Easy Install Gutter Protection',
    twitterDescription: 'Low-cost, easy-install gutter leaf protection. Available in 6 diameters. Made in USA since 2004.',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebSite',
          '@id': `${siteUrl}/#website`,
          url: siteUrl,
          name: 'GutterBrush Simple Gutter Guards',
          description: 'Low-cost, easy-install gutter leaf protection made in USA since 2004.',
          publisher: {
            '@id': `${siteUrl}/#organization`,
          },
          potentialAction: {
            '@type': 'SearchAction',
            target: {
              '@type': 'EntryPoint',
              urlTemplate: `${siteUrl}/?s={search_term_string}`,
            },
            'query-input': 'required name=search_term_string',
          },
        },
        {
          '@type': 'WebPage',
          '@id': `${siteUrl}/#webpage`,
          url: `${siteUrl}/`,
          name: 'GutterBrush Simple Gutter Guards - Easy Install Gutter Protection Since 2004',
          description: 'GutterBrush Simple Gutter Guards offers low-cost, easy-install gutter leaf protection. Available in 6 diameters for any residential or commercial gutter. Made in USA since 2004.',
          isPartOf: { '@id': `${siteUrl}/#website` },
          about: { '@id': `${siteUrl}/#organization` },
          datePublished: '2004-01-01',
          dateModified: '2026-03-17',
          inLanguage: 'en-US',
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: `${siteUrl}/`,
              },
            ],
          },
        },
        {
          '@type': 'Organization',
          '@id': `${siteUrl}/#organization`,
          name: 'GutterBrush Simple Gutter Guards',
          legalName: 'GutterBrush Simple Gutter Guards',
          url: siteUrl,
          foundingDate: '2004',
          description: 'American manufacturer of simple gutter leaf guards since 2004. Low-cost, easy-install gutter protection available in 6 diameters for residential and commercial gutters.',
          address: {
            '@type': 'PostalAddress',
            addressCountry: 'US',
          },
          contactPoint: [
            {
              '@type': 'ContactPoint',
              contactType: 'customer service',
              availableLanguage: 'English',
              url: 'https://www.gutterbrush.com/pages/request-a-free-gutterbrush-quote',
            },
          ],
          sameAs: [
            'https://www.gutterbrush.com',
          ],
        },
        {
          '@type': 'ItemList',
          name: 'GutterBrush Gutter Guard Products',
          description: 'Available in 6 diameters to protect any residential or commercial gutter system.',
          numberOfItems: 3,
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              item: {
                '@type': 'Product',
                name: 'GutterBrush Residential Gutter Guards',
                description: 'Fits standard 5" and 6" residential gutters. Easy to install, effective at blocking leaves while allowing water flow.',
                brand: { '@type': 'Brand', name: 'GutterBrush' },
                manufacturer: { '@id': `${siteUrl}/#organization` },
                url: 'https://www.gutterbrush.com/pages/how-to-measure-for-gutter-guards',
                category: 'Gutter Guards',
                countryOfOrigin: 'US',
                offers: {
                  '@type': 'AggregateOffer',
                  priceCurrency: 'USD',
                  priceRange: '$300-$400',
                  availability: 'https://schema.org/InStock',
                },
              },
            },
            {
              '@type': 'ListItem',
              position: 2,
              item: {
                '@type': 'Product',
                name: 'GutterBrush Large Commercial Gutter Guards',
                description: 'Heavy-duty gutter protection for commercial buildings and larger residential properties. Available in diameters from 6.5" to 8.0".',
                brand: { '@type': 'Brand', name: 'GutterBrush' },
                manufacturer: { '@id': `${siteUrl}/#organization` },
                url: 'https://www.gutterbrush.com/products/commercial-gutter-guards',
                category: 'Gutter Guards',
                countryOfOrigin: 'US',
                offers: {
                  '@type': 'AggregateOffer',
                  priceCurrency: 'USD',
                  availability: 'https://schema.org/InStock',
                },
              },
            },
            {
              '@type': 'ListItem',
              position: 3,
              item: {
                '@type': 'Product',
                name: 'GutterBrush Custom Gutter Solutions',
                description: 'Special sizing and configurations for unique gutter systems. Expert consultation to determine the perfect fit.',
                brand: { '@type': 'Brand', name: 'GutterBrush' },
                manufacturer: { '@id': `${siteUrl}/#organization` },
                url: 'https://www.gutterbrush.com/products/5-inch-gutter-guards',
                category: 'Gutter Guards',
                countryOfOrigin: 'US',
                offers: {
                  '@type': 'AggregateOffer',
                  priceCurrency: 'USD',
                  availability: 'https://schema.org/InStock',
                },
              },
            },
          ],
        },
        {
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'How much does GutterBrush cost for most homes?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Most homes are protected for $300-$400 with GutterBrush gutter guards, making it 90% less expensive than alternatives like Leaf Filter.',
              },
            },
            {
              '@type': 'Question',
              name: 'Do I need special tools to install GutterBrush gutter guards?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'No special tools are required. GutterBrush simply slides into your existing gutters with no cutting, fasteners, or professional installation needed.',
              },
            },
            {
              '@type': 'Question',
              name: 'What gutter sizes does GutterBrush fit?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'GutterBrush is available in 6 diameters fitting gutters from 2.25" up to 8.0", covering both standard residential (5" and 6") and large commercial gutters.',
              },
            },
            {
              '@type': 'Question',
              name: 'Where is GutterBrush made?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'GutterBrush has been proudly made in the USA since 2004, offering American-made quality and proven performance for nearly two decades.',
              },
            },
          ],
        },
      ],
    },
  });

  return (
    <main>
      <HeroSection />
      <PainPointsSection />
      <ProductsSection />
      <BenefitsSection />
      <ProcessSection />
      <TestimonialsSection />
      <GallerySection />
      <AboutSection />
      <ContactSection />
    </main>
  );
};

export default HomePage;
